import React, { useState, useRef, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setTermsModal } from "../../ReduxSlices/ModalSlice";
import { useTermsAcceptance } from "../../hooks/useTermsAcceptance";
import { TERMS_VERSION, TERMS_HASH, TERMS_LAST_MODIFIED, TERMS_SECTIONS } from "../../utils/termsContent";
import { X } from "lucide-react";
import ClipLoader from "react-spinners/ClipLoader";

const TermsModal = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.modal.termsModal);
  const account = useSelector((state) => state.auth.account);
  const { signing, signAndAcceptTerms } = useTermsAcceptance();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom) setHasScrolledToBottom(true);
  }, []);

  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [open]);

  const handleAccept = async () => {
    await signAndAcceptTerms();
  };

  const handleDecline = () => {
    dispatch(setTermsModal(false));
  };

  if (!open || !account) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="w-[95vw] sm:w-[90vw] md:w-[640px] max-w-[640px] rounded-[16px] bg-white shadow-xl border-0 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Terms of Use</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Last Modified: {TERMS_LAST_MODIFIED} | Version {TERMS_VERSION}
            </p>
          </div>
          <button
            onClick={handleDecline}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Terms Content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-700 leading-relaxed"
          style={{ maxHeight: "55vh" }}
        >
          {TERMS_SECTIONS.map((section, idx) => (
            <div key={idx} className="mb-5">
              {section.title && (
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">{section.title}</h3>
              )}
              <div className="whitespace-pre-line text-[13px] leading-[1.7]">
                {section.content}
              </div>
            </div>
          ))}

          <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-400">
            <p>Terms Hash: {TERMS_HASH}</p>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="px-6 py-4 border-t border-gray-200 space-y-3 flex-shrink-0">
          {!hasScrolledToBottom && (
            <p className="text-xs text-gray-500 text-center">
              Please scroll to the bottom to continue
            </p>
          )}
          <div className="flex gap-3">
            <button
              onClick={handleDecline}
              className="flex-1 py-2.5 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={!hasScrolledToBottom || signing}
              className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                hasScrolledToBottom && !signing
                  ? "bg-black text-white hover:bg-gray-800"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {signing ? (
                <>
                  <ClipLoader size={16} color="#999" />
                  <span>Signing...</span>
                </>
              ) : (
                "I Accept & Sign"
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center">
            Your wallet will prompt you to sign a message. This does not cost any gas.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
